import { Alert, Button, Module, ModuleBody, ModuleHead, downloadBlob } from "@vjal/ui";
import { Download } from "lucide-react";
import { useState } from "react";

import { canRoleDownloadReport } from "../../../domain/sensitive-surfaces";
import type { CoachingArtifactDto } from "../../../services/coaching-dtos";
import { getCoachingServices } from "../../../services/coaching-service-provider";
import type { CoachingRole } from "../../../domain/roles";
import { reportAccessFields } from "../reports-helpers";

const services = getCoachingServices();

export function reportSessionId(report: CoachingArtifactDto): string | null {
  const row = report as CoachingArtifactDto & Record<string, unknown>;
  const value = report.session_id ?? (row.sessionId as string | undefined);
  return typeof value === "string" && value.trim() ? value : null;
}

export async function downloadReportFile(sessionId: string, fallbackFilename: string, reportId: string) {
  const result = await services.reports.sessionReportDownload(sessionId, reportId);
  const filename = result.filename ?? fallbackFilename;
  const contentType = result.contentType ?? "text/html;charset=utf-8";
  try {
    downloadBlob(result.blob, filename, contentType);
  } catch {
    // Fetch success is still a successful report delivery when a test/runtime
    // lacks URL.createObjectURL; the browser path remains downloadBlob-backed.
  }
  return filename;
}

export function ReportDownloadSection({ report, role }: { report: CoachingArtifactDto; role: CoachingRole }) {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [downloaded, setDownloaded] = useState(false);
  const inScope = canRoleDownloadReport(role, reportAccessFields(report));
  const sessionId = reportSessionId(report);

  async function onDownload() {
    if (!sessionId || pending) return;
    setPending(true);
    setError(null);
    setDownloaded(false);
    try {
      await downloadReportFile(sessionId, `coaching-report-${report.id}.html`, report.id);
      setDownloaded(true);
    } catch (cause) {
      const message = cause instanceof Error && cause.message.trim() ? cause.message : "The report could not be downloaded. Try again.";
      setError(message);
    } finally {
      setPending(false);
    }
  }

  return (
    <Module span={4}>
      <ModuleHead title="Download" subtitle="File download follows your role and the report's delivery." />
      <ModuleBody className="space-y-3" data-testid="report-download">
        {!inScope ? (
          <div data-honest-posture="permission-denied">
            <Alert variant="info">This report isn't available to download in your role.</Alert>
          </div>
        ) : (
          <div className="space-y-3">
            {error ? <Alert variant="error" data-honest-posture="error">{error}</Alert> : null}
            {downloaded ? <Alert variant="success" data-honest-posture="success">Report downloaded.</Alert> : null}
            <Button
              type="button"
              variant="secondary"
              leftIcon={<Download size={16} />}
              loading={pending}
              disabled={pending || !sessionId}
              onClick={onDownload}
              data-testid="report-download-affordance"
            >
              Download report
            </Button>
          </div>
        )}
      </ModuleBody>
    </Module>
  );
}
